import { createBrowserRouter } from 'zf-web-infra';
import { MF_REMOTES } from 'config';
import { loadRemote } from '@module-federation/runtime';
import { Layout } from '@components';
import { NotFound, RouteException } from '../error';
import { IframeRouters } from '../iframe';
import { LoginRouter } from '../login';
import { HomeRouter } from '../home';
import { PrivacyRouter } from '../privacy';
import { AgreementRouter } from '../agreement';
import type { Remote, RemoteEntry } from './types';
import type { PatchRoutesOnNavigationFunctionArgs, RouteObject } from 'react-router';

class RouteBuilder {

  routerInstance: ReturnType<typeof createBrowserRouter> | null = null;

  /**
   * 路由的作用是让页面能够根据 URL 的变化进行页面的切换
   * 路由机制提供了按页面去组织整个应用程序的能力，页面之间的交互可以主要通过 URL 来进行，
   * 从而可以让各个业务功能互相独立，实现逻辑的解耦.
   */
  private readonly routes: RouteObject[] = [
    {
      id: 'root',
      path: '/',
      element: (<Layout />),
      errorElement: (<RouteException />),
      children: [
        HomeRouter,
        ...IframeRouters
      ]
    },
    LoginRouter,
    PrivacyRouter,
    AgreementRouter,
    // 顶级路由占位，防止匹配到 root 下，加载出菜单和头
    {
      id: 'toolkit',
      path: 'toolkit',
      children: []
    },
    {
      id: 'training',
      path: 'v3',
      children: []
    },
    {
      id: 'analyze',
      path: 'analyze',
      children: []
    },
    {
      id: 'shoteyes',
      path: 'v2',
      children: []
    },
    {
      id: 'detective',
      path: 'detective',
      children: []
    }
  ];

  /**
   * 这里的 createBrowserRouter 是被 Sentry 包裹的，用于捕获路由异常
   */
  build(): ReturnType<typeof createBrowserRouter> {
    if (this.routerInstance == null) {
      this.routerInstance = createBrowserRouter(this.routes, {
        patchRoutesOnNavigation: async ({ patch, path }: PatchRoutesOnNavigationFunctionArgs) => {
          const remoteEntry = this.getRemoteEntry(path);
          if (path.startsWith('/shoteyes/') || path.startsWith('/training/')) {
            return;
          }
          if (remoteEntry == null) {
            // 没有找到路由返回 404
            return patch(null, [{ path, element: <NotFound /> }]);
          }
          const remote = await loadRemote<Remote>(remoteEntry.entry.expose);
          if (remote?.Route == null) {
            // 远程模块加载失败返回 404
            return patch(null, [{ path, element: <NotFound /> }]);
          }
          if (remoteEntry.entry.top) {
            // 挂载在指定的 id 的顶级路由下
            return patch(remoteEntry.name, [remote.Route]);
          }
          return patch('root', [remote.Route]);
        }
      });
    }
    return this.routerInstance;
  }

  /**
   * 根据当前路径，匹配远程微应用暴露的入口
   */
  private getRemoteEntry(path: string): RemoteEntry | undefined {
    for (const remote of MF_REMOTES) {
      if (!remote.enable) {
        continue;
      }
      for (const item of remote.exposes) {
        // 顶层路由、开启严格匹配的情况
        if (item.top && item.strict === true) {
          if (item.prefix.includes(path)) {
            return { name: remote.name, entry: item };
          }
          continue;
        }
        for (const prefix of item.prefix) {
          if (path.startsWith(prefix)) {
            return { name: remote.name, entry: item };
          }
        }
      }
    }
  }

}

export const router = new RouteBuilder();
